import { Home, MessageCircle, Dumbbell, TrendingUp, AlertTriangle, HeartPulse, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { Analytics } from "@/lib/analytics";
import { SAFE_AREA } from "@/utils/safeArea";
import { useProfile } from "@/hooks/useProfile";

export const BottomNavigation = () => {
  const { profile } = useProfile();
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const [showWorkoutWarning, setShowWorkoutWarning] = useState(false);
  const [showRunningWarning, setShowRunningWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string>("");
  const [hasActiveWorkout, setHasActiveWorkout] = useState(false);
  const [hasActiveRun, setHasActiveRun] = useState(false);

  useEffect(() => {
    const checkActiveSessions = () => {
      const activeWorkout = localStorage.getItem('activeWorkoutSession');
      const activeRun = localStorage.getItem('activeRunningSession');
      setHasActiveWorkout(!!activeWorkout);
      setHasActiveRun(!!activeRun);
    };
    checkActiveSessions();
    const interval = setInterval(checkActiveSessions, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleNavigation = (path: string) => {
    // Check for active gym workout
    if (location.pathname === '/gym-workout' && hasActiveWorkout && path !== '/gym-workout') {
      setPendingNavigation(path);
      setShowWorkoutWarning(true);
      return;
    }
    
    // Check for active running session
    if (location.pathname === '/running-active' && hasActiveRun && path !== '/running-active') {
      const targetTab = navItems.find(item => item.path === path)?.label || path;
      Analytics.runningTabSwitchAttempted(targetTab);
      setPendingNavigation(path);
      setShowRunningWarning(true);
      return;
    }
    
    const tabName = navItems.find(item => item.path === path)?.label || path;
    Analytics.tabChanged(tabName);
    navigate(path);
  };

  const confirmNavigation = () => {
    setShowWorkoutWarning(false);
    navigate(pendingNavigation);
  };

  const confirmRunningNavigation = () => {
    const targetTab = navItems.find(item => item.path === pendingNavigation)?.label || pendingNavigation;
    Analytics.runningTabSwitchConfirmed(targetTab);
    setShowRunningWarning(false);
    // Keep the run session - user can return to it
    navigate(pendingNavigation);
  };

  const cancelRunningNavigation = () => {
    Analytics.runningTabSwitchCancelled();
    setShowRunningWarning(false);
    setPendingNavigation('');
  };

  const endRunAndNavigate = () => {
    const targetTab = navItems.find(item => item.path === pendingNavigation)?.label || pendingNavigation;
    Analytics.runningTabSwitchConfirmed(targetTab);
    Analytics.runningWorkoutAbandoned(0, 0, 'tab_switch_end');
    localStorage.removeItem('activeRunningSession');
    setHasActiveRun(false);
    setShowRunningWarning(false);
    navigate(pendingNavigation);
  };

  const deleteWorkout = () => {
    localStorage.removeItem('activeWorkoutSession');
    setHasActiveWorkout(false);
    setShowWorkoutWarning(false);
    navigate(pendingNavigation);
  };

  const isIOS = Capacitor.getPlatform() === 'ios';
  const iconSize = isIOS ? 30 : 24;

  const navItems = [
    { icon: Home, label: t('nav.home'), path: "/dashboard", tutorialId: "dashboard" },
    { icon: MessageCircle, label: profile?.ai_coach_name || t('nav.coachDefaultName'), path: "/chat", tutorialId: "chat" },
    { icon: Dumbbell, label: t('nav.workouts'), path: "/workouts", tutorialId: "workouts" },
    { icon: HeartPulse, label: t('nav.health'), path: "/food-log", tutorialId: "nutrition" },
    { icon: TrendingUp, label: t('nav.progress'), path: "/progress", tutorialId: "progress" },
  ];

  return (
    <>
      <nav 
        className="fixed left-0 right-0 backdrop-blur-xl z-[9999]"
        style={{
          bottom: 0,
          height: `calc(56px + ${SAFE_AREA.bottom})`,
          paddingLeft: SAFE_AREA.left,
          paddingRight: SAFE_AREA.right,
          paddingBottom: SAFE_AREA.bottom,
          backgroundColor: isDark ? 'rgba(10, 10, 14, 0.6)' : 'var(--nav-bg)',
          borderTop: 'none',
          boxShadow: isDark 
            ? 'inset 0 1px 0 0 rgba(255,255,255,0.05)' 
            : '0 -4px 24px hsla(220, 20%, 50%, 0.06)',
        }}
      >
        <div 
          className="flex justify-around items-center w-full px-1 sm:px-2"
          style={{ 
            minHeight: '56px',
            height: '56px',
          }}
        >
          {navItems.map(({ icon: Icon, label, path, tutorialId }) => {
            const isActive = location.pathname === path || (path === '/food-log' && location.pathname === '/health-data');
            const isWorkoutActive = path === '/workouts' && hasActiveWorkout;
            
            return (
              <button
                key={path}
                onClick={() => handleNavigation(path)}
                data-tutorial={tutorialId}
                className={`relative flex flex-1 flex-col items-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg transition-all duration-300 hover:bg-muted/20 ${
                  isActive 
                    ? (isDark ? "text-primary" : "text-nav-text-active") 
                    : (isDark ? "text-muted-foreground" : "text-nav-text")
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="relative">
                  <Icon size={iconSize} className="sm:w-5 sm:h-5" strokeWidth={isActive ? 1.5 : 1.25} />
                  {isWorkoutActive && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-gold rounded-full animate-pulse shadow-gold" />
                  )}
                </div>
                <span className="text-[10px] sm:text-xs font-medium leading-tight">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <AlertDialog open={showWorkoutWarning} onOpenChange={setShowWorkoutWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('nav.activeWorkoutTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('nav.activeWorkoutDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowWorkoutWarning(false)}>
              {t('nav.stayInWorkout')}
            </AlertDialogCancel>
            <Button 
              variant="destructive" 
              onClick={deleteWorkout}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {t('nav.deleteWorkout')}
            </Button>
            <AlertDialogAction onClick={confirmNavigation}>
              {t('nav.continueSaveProgress')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Running Session Warning */}
      <AlertDialog open={showRunningWarning} onOpenChange={setShowRunningWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('nav.runInProgressTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('nav.runInProgressDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row gap-3 sm:gap-3 items-center justify-center mt-4">
            <AlertDialogCancel 
              onClick={cancelRunningNavigation}
              className="flex-1 m-0 h-11"
            >
              {t('nav.backToRun')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRunningNavigation}
              className="flex-1 m-0 h-11 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('nav.leave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
